import { Icon } from '@mdi/react';
import { Stack } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { IAppReduxStore } from '../../../redux/store';
import { vars } from '../../../Theme';
import AppTooltip, { TooltipPlacement } from '../AppTooltip/AppTooltip';

/**
 * Icon that shows status informtion for a document signature.
 */
export default ({
    status,
    tooltip,
    tooltipPlacement,
}: {
    status: string;
    tooltip?: boolean;
    tooltipPlacement?: TooltipPlacement;
}) => {
    /** Configure component translation. */
    const { i18n } = useTranslation();

    /** Get relevant values from Redux store. */
    const signatureStatuses = useSelector(
        (store: IAppReduxStore) => store.projectSignatureStatus.data,
    );

    /** Status not provided or backend data is not yet loaded - stop here. */
    if (!status || !signatureStatuses.length) {
        return <></>;
    }

    /** Find matching signature status record using given status id. */
    const match = signatureStatuses.filter(x => x.status === status)[0];

    if (!match) {
        /** Maybe return a default signature status? */
        return <></>;
    }

    /** Create the status icon itself. */
    const statusIcon = (
        <Stack
            sx={{
                flex: 0,
            }}
        >
            <Icon path={match.icon} color={match.color ?? vars.font} size={1} />
        </Stack>
    );

    /** Wrap icon in a tooltip, if needed. */
    return tooltip || tooltipPlacement ? (
        <AppTooltip
            title={match.translations[i18n.language]}
            placement={tooltipPlacement}
            children={statusIcon}
        />
    ) : (
        statusIcon
    );
};

/**
 * Interface that describes the props for a SignatureStatusIcon component.
 */
export interface ISignatureStatusIconProps {
    /** Signature Status Id. */
    status: string;

    /** (Optional) Whether to show a tooltip or not. */
    tooltip?: boolean;

    /** (Optional) Icon Tooltip Placement. */
    tooltipPlacement?: TooltipPlacement;
}
